import React, { useState, useEffect, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { analyticsAPI } from '../services/api';
import './Dashboard.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const [gameType, setGameType] = useState('drill'); // 'drill' or 'cue_game'
  const [days, setDays] = useState(30);
  const [overviewData, setOverviewData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load analytics data
  const loadAnalyticsData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('Loading analytics data...');
      
      // Check authentication status
      const token = localStorage.getItem('access_token');
      console.log('Auth token exists:', !!token);
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      console.log('Fetching overview data...');
      const overview = await analyticsAPI.getOverview(days, gameType);
      console.log('Overview data received:', overview);
      setOverviewData(overview);
    } catch (err) {
      console.error('Error loading analytics data:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.status,
        data: err.data
      });

      if (err.status === 400) {
        setError(`Analytics rejected the overview request for ${gameType}. ${err.message || ''}`.trim());
      } else {
        setError(`Failed to load analytics data: ${err.message || 'Unknown error'}`);
      }
    } finally {
      setIsLoading(false);
    }
  }, [days, gameType]);

  useEffect(() => {
    loadAnalyticsData();
  }, [loadAnalyticsData]);

  const themeStyles =
    typeof window !== 'undefined' && typeof document !== 'undefined'
      ? window.getComputedStyle(document.documentElement)
      : null;

  const getThemeColor = (name, fallback) =>
    themeStyles?.getPropertyValue(name)?.trim() || fallback;

  const chartTheme = {
    textPrimary: getThemeColor('--text-1', '#e5eefb'),
    textSecondary: getThemeColor('--text-2', '#a7b7cf'),
    border: getThemeColor('--border-1', '#26344d'),
    surface: getThemeColor('--surface-1', '#111827'),
    grid: 'rgba(134, 149, 171, 0.18)',
  };

  // Chart configurations
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: chartTheme.textSecondary,
        },
      },
      tooltip: {
        backgroundColor: chartTheme.surface,
        titleColor: chartTheme.textPrimary,
        bodyColor: chartTheme.textSecondary,
        borderColor: chartTheme.border,
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        ticks: {
          color: chartTheme.textSecondary,
        },
        grid: {
          color: chartTheme.grid,
        },
        border: {
          color: chartTheme.border,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: chartTheme.textSecondary,
        },
        grid: {
          color: chartTheme.grid,
        },
        border: {
          color: chartTheme.border,
        },
      },
    },
  };

  const lineChartOptions = {
    ...chartOptions,
    scales: {
      ...chartOptions.scales,
      y: {
        ...chartOptions.scales.y,
        beginAtZero: true,
        max: 10, // Scores are on 0-10 scale
      },
    },
  };

  const byTagData = Array.isArray(overviewData?.questions_by_tag) ? overviewData.questions_by_tag : [];
  const timeSeriesData = {
    recall_scores: Array.isArray(overviewData?.recall_scores_over_time)
      ? overviewData.recall_scores_over_time
      : [],
    creativity_scores: Array.isArray(overviewData?.creativity_scores_over_time)
      ? overviewData.creativity_scores_over_time
      : [],
    overall_scores: Array.isArray(overviewData?.overall_scores_over_time)
      ? overviewData.overall_scores_over_time
      : [],
    response_times: Array.isArray(overviewData?.response_times_over_time)
      ? overviewData.response_times_over_time
      : []
  };

  // Prepare data for charts
  const getQuestionsByTagData = () => {
    if (!byTagData.length) return { labels: [], datasets: [] };

    return {
      labels: byTagData.map(tag => tag.tag_name),
      datasets: [
        {
          label: 'Questions Answered',
          data: byTagData.map(tag => tag.value ?? tag.total_questions_answered ?? 0),
          backgroundColor: [
            'rgba(54, 162, 235, 0.6)',
            'rgba(255, 99, 132, 0.6)',
            'rgba(255, 205, 86, 0.6)',
            'rgba(75, 192, 192, 0.6)',
            'rgba(153, 102, 255, 0.6)',
          ],
          borderColor: [
            'rgba(54, 162, 235, 1)',
            'rgba(255, 99, 132, 1)',
            'rgba(255, 205, 86, 1)',
            'rgba(75, 192, 192, 1)',
            'rgba(153, 102, 255, 1)',
          ],
          borderWidth: 1,
        },
      ],
    };
  };

  const getTimeSeriesChartData = (data, label, color) => {
    if (!data.length) return { labels: [], datasets: [] };

    // Group data by tag to create separate lines
    const tagGroups = {};
    data.forEach(item => {
      if (!tagGroups[item.tag_name]) {
        tagGroups[item.tag_name] = [];
      }
      tagGroups[item.tag_name].push(item);
    });

    // Get all unique dates and sort them
    const allDates = [...new Set(data.map(item => item.date))].sort();
    const labels = allDates.map(date => new Date(date).toLocaleDateString());

    // Create datasets for each tag
    const datasets = [];
    const colors = [
      'rgb(54, 162, 235)',   // Blue
      'rgb(255, 99, 132)',   // Red
      'rgb(255, 205, 86)',   // Yellow
      'rgb(75, 192, 192)',   // Teal
      'rgb(153, 102, 255)',  // Purple
      'rgb(255, 159, 64)',   // Orange
      'rgb(199, 199, 199)',  // Grey
    ];

    Object.keys(tagGroups).forEach((tagName, index) => {
      const tagData = tagGroups[tagName];
      const tagColor = colors[index % colors.length];
      
      // Create data array aligned with all dates
      const tagValues = allDates.map(date => {
        const dataPoint = tagData.find(item => item.date === date);
        return dataPoint ? dataPoint.value : null;
      });

      datasets.push({
        label: `${tagName} - ${label}`,
        data: tagValues,
        borderColor: tagColor,
        backgroundColor: tagColor.replace('rgb', 'rgba').replace(')', ', 0.1)'),
        tension: 0.1,
        spanGaps: true, // Connect points even when there are gaps
      });
    });

    return {
      labels,
      datasets,
    };
  };

  if (isLoading) {
    return (
      <div className="dashboard-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={loadAnalyticsData} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const totalQuestionsAnswered = overviewData?.overview?.total_questions_answered ?? 0;
  const hasNoData = !isLoading && !!overviewData && totalQuestionsAnswered === 0;

  return (
    <div className="dashboard-container">
      {/* Header Controls */}
      <div className="analytics-header">
        <h2>Performance Analytics</h2>
        <div className="dashboard-controls">
          <div className="control-group">
            <label>Game Type:</label>
            <div className="game-type-selector">
              <button 
                className={`arrow-button ${gameType === 'drill' ? 'active' : ''}`}
                onClick={() => setGameType('drill')}
                title="Recall Drill"
              >
                ← Recall Drill
              </button>
              <button 
                className={`arrow-button ${gameType === 'cue_game' ? 'active' : ''}`}
                onClick={() => setGameType('cue_game')}
                title="Cue Game"
              >
                Cue Game →
              </button>
            </div>
          </div>
          <div className="control-group">
            <label htmlFor="days">Time Period:</label>
            <select
              id="days"
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              className="days-selector"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
        </div>
      </div>

      {hasNoData ? (
        <div className="empty-state">
          <h3>No Analytics Data Yet</h3>
          <p>You haven't completed any learning sessions yet. Start practicing to see your performance analytics!</p>
          <div className="empty-state-actions">
            <button onClick={() => window.location.href = '/practice'} className="start-practice-button">
              Start Practicing
            </button>
          </div>
        </div>
      ) : (
      <>
      {/* Overview Stats */}
      {overviewData?.overview && (
        <div className="overview-stats">
          <div className="stat-card">
            <h3>Total Questions</h3>
            <div className="stat-value">{totalQuestionsAnswered}</div>
          </div>
          {gameType === 'cue_game' ? (
            <>
              <div className="stat-card">
                <h3>Avg Creativity Score</h3>
                <div className="stat-value">{overviewData.overview.average_creativity_score?.toFixed(1) || 'N/A'}</div>
              </div>
              <div className="stat-card">
                <h3>Avg Final Score</h3>
                <div className="stat-value">{overviewData.overview.average_overall_score?.toFixed(1) || 'N/A'}</div>
              </div>
            </>
          ) : (
            <>
              <div className="stat-card">
                <h3>Avg Recall Score</h3>
                <div className="stat-value">{overviewData.overview.average_recall_score?.toFixed(1) || 'N/A'}</div>
              </div>
              <div className="stat-card">
                <h3>Avg Creativity Score</h3>
                <div className="stat-value">{overviewData.overview.average_creativity_score?.toFixed(1) || 'N/A'}</div>
              </div>
            </>
          )}
          <div className="stat-card">
            <h3>Avg Response Time</h3>
            <div className="stat-value">{overviewData.overview.average_response_time?.toFixed(1) || 'N/A'}s</div>
          </div>
        </div>
      )}

      {/* Charts Grid */}
      <div className="charts-grid">
        {/* Questions Answered by Tag */}
        <div className="chart-container">
          <h3>Questions Answered by Tag</h3>
          <div className="chart-wrapper">
            <Bar data={getQuestionsByTagData()} options={chartOptions} />
          </div>
        </div>

        {/* Recall/Final Scores Over Time */}
        {gameType === 'cue_game' ? (
          <div className="chart-container">
            <h3>Final Scores Over Time</h3>
            <div className="chart-wrapper">
              <Line 
                data={getTimeSeriesChartData(
                  timeSeriesData.overall_scores, 
                  'Final Score'
                )} 
                options={lineChartOptions} 
              />
            </div>
          </div>
        ) : (
          <div className="chart-container">
            <h3>Recall Scores Over Time</h3>
            <div className="chart-wrapper">
              <Line 
                data={getTimeSeriesChartData(
                  timeSeriesData.recall_scores, 
                  'Recall Score'
                )} 
                options={lineChartOptions} 
              />
            </div>
          </div>
        )}

        {/* Creativity Scores Over Time */}
        <div className="chart-container">
          <h3>Creativity Scores Over Time</h3>
          <div className="chart-wrapper">
            <Line 
              data={getTimeSeriesChartData(
                timeSeriesData.creativity_scores, 
                'Creativity Score'
              )} 
              options={lineChartOptions} 
            />
          </div>
        </div>

        {/* Response Times Over Time */}
        <div className="chart-container">
          <h3>Response Times Over Time</h3>
          <div className="chart-wrapper">
            <Line 
              data={getTimeSeriesChartData(
                timeSeriesData.response_times, 
                'Response Time (seconds)'
              )} 
              options={{
                ...chartOptions,
                scales: {
                  ...chartOptions.scales,
                  y: {
                    ...chartOptions.scales.y,
                    beginAtZero: true,
                  },
                },
              }} 
            />
          </div>
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default Dashboard;
